import { useState } from "react";
import { Mail, Phone, Building2, GripVertical, Trash2 } from "lucide-react";

const COLUMNS = [
  { id: "nuevo", label: "Nuevo", accent: "bg-blue-500", soft: "bg-blue-50 border-blue-200" },
  { id: "contactado", label: "Contactado", accent: "bg-amber-500", soft: "bg-amber-50 border-amber-200" },
  { id: "reunion", label: "En reunión", accent: "bg-purple-500", soft: "bg-purple-50 border-purple-200" },
  { id: "ganado", label: "Ganado", accent: "bg-emerald-500", soft: "bg-emerald-50 border-emerald-200" },
  { id: "perdido", label: "Perdido", accent: "bg-slate-400", soft: "bg-slate-100 border-slate-300" },
];

export default function LeadsKanban({ leads, onStatusChange, onOpen, onDelete }) {
  const [draggingId, setDraggingId] = useState(null);
  const [overColumn, setOverColumn] = useState(null);

  const handleDrop = (columnId) => {
    setOverColumn(null);
    const lead = leads.find((l) => l.id === draggingId);
    setDraggingId(null);
    if (!lead) return;
    if ((lead.status || "nuevo") === columnId) return;
    onStatusChange(lead, columnId);
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4" data-testid="leads-kanban">
      {COLUMNS.map((col) => {
        const items = leads.filter((l) => (l.status || "nuevo") === col.id);
        const isOver = overColumn === col.id;
        return (
          <div
            key={col.id}
            onDragOver={(e) => {
              e.preventDefault();
              if (overColumn !== col.id) setOverColumn(col.id);
            }}
            onDragLeave={() => setOverColumn((c) => (c === col.id ? null : c))}
            onDrop={() => handleDrop(col.id)}
            className={`rounded-2xl border transition-colors ${
              isOver ? `${col.soft} border-dashed` : "bg-white border-slate-200"
            }`}
            data-testid={`kanban-column-${col.id}`}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${col.accent}`} />
                <h3 className="font-heading font-semibold text-sm text-galvani-text">{col.label}</h3>
              </div>
              <span
                className="font-body text-xs font-semibold text-galvani-textLight bg-slate-100 rounded-full px-2 py-0.5"
                data-testid={`kanban-count-${col.id}`}
              >
                {items.length}
              </span>
            </div>

            <div className="p-3 space-y-3 min-h-[160px]">
              {items.length === 0 && (
                <p className="font-body text-xs text-slate-400 text-center py-6">
                  Arrastra leads aquí
                </p>
              )}
              {items.map((lead) => (
                <div
                  key={lead.id}
                  draggable
                  onDragStart={() => setDraggingId(lead.id)}
                  onDragEnd={() => {
                    setDraggingId(null);
                    setOverColumn(null);
                  }}
                  className={`group rounded-xl border border-slate-200 bg-white p-3 shadow-sm hover:shadow-md cursor-grab active:cursor-grabbing transition-[box-shadow,opacity] ${
                    draggingId === lead.id ? "opacity-40" : ""
                  }`}
                  data-testid={`kanban-card-${lead.id}`}
                >
                  <div className="flex items-start gap-2">
                    <GripVertical className="w-4 h-4 text-slate-300 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => onOpen(lead)}
                        className="text-left w-full"
                        data-testid={`kanban-open-${lead.id}`}
                      >
                        <p className="font-body font-semibold text-sm text-galvani-text truncate group-hover:text-galvani-purple transition-colors">
                          {lead.nombre}
                        </p>
                      </button>
                      {lead.empresa && (
                        <p className="font-body text-xs text-galvani-textLight flex items-center gap-1 mt-1 truncate">
                          <Building2 className="w-3 h-3 flex-shrink-0" />
                          {lead.empresa}
                        </p>
                      )}
                      <div className="mt-2 flex flex-wrap items-center gap-1.5">
                        {lead.origen === "agencias" && (
                          <span className="text-[10px] uppercase tracking-wider bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded font-semibold">
                            Agencia
                          </span>
                        )}
                        {!lead.qualified && (
                          <span className="text-[10px] uppercase tracking-wider bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded font-semibold">
                            Bajo ppto
                          </span>
                        )}
                        {lead.modalidad === "estudio" && (
                          <span className="text-[10px] uppercase tracking-wider bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-semibold">
                            Estudio
                          </span>
                        )}
                        {lead.modalidad === "desplazamiento" && (
                          <span className="text-[10px] uppercase tracking-wider bg-cyan-100 text-cyan-700 px-1.5 py-0.5 rounded font-semibold">
                            Desplaz.
                          </span>
                        )}
                      </div>
                      <div className="mt-2 flex items-center gap-3">
                        <a
                          href={`mailto:${lead.email}`}
                          className="text-slate-400 hover:text-galvani-purple transition-colors"
                          title={lead.email}
                        >
                          <Mail className="w-3.5 h-3.5" />
                        </a>
                        <a
                          href={`tel:${lead.telefono}`}
                          className="text-slate-400 hover:text-galvani-purple transition-colors"
                          title={lead.telefono}
                        >
                          <Phone className="w-3.5 h-3.5" />
                        </a>
                        <button
                          onClick={() => onDelete(lead)}
                          className="text-slate-400 hover:text-red-600 transition-colors ml-auto"
                          title="Eliminar"
                          data-testid={`kanban-delete-${lead.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
